import type { ReactNode } from "react";
import { Check, X } from "lucide-react";
import type { BrokerOutreachReadinessDto } from "@/api/brokerOutreach";
import { buttonVariants } from "@/design-system/components/ui";
import { brokerOutreachCopy } from "./brokerOutreachCopy";

/**
 * D24 — what the Broker intro tab can reach the listing broker WITH, at the top
 * of the tab: the agent, their email, their phone and its type, and the
 * listing's contact form.
 *
 * <p>🔴 <b>FIND AGENT opens a NEW BROWSER TAB (R6).</b> The research workspace
 * is a full page; navigating this tab there would cost the consultant the
 * submission they are working (and, in a drawer, the buyer underneath it). An
 * anchor with <c>target="_blank"</c> and <c>rel="noopener noreferrer"</c>, so
 * the new page holds no handle back to this one. The query behind this strip
 * refetches on window focus, so coming back from that tab shows what was
 * found.</p>
 *
 * <p>The facts are server-sourced from the listing's own agent and
 * <c>Listing.ContactFormUrl</c>; a firm contact's label is already
 * "Firm contact — {Firm}".</p>
 */
export function ReadinessStrip({ readiness }: { readiness: BrokerOutreachReadinessDto }) {
  const phoneLabel = readiness.hasPhone
    ? readiness.phoneType === "Unknown"
      ? "Phone (type unknown)"
      : `Phone (${readiness.phoneType.toLowerCase()})`
    : "Phone";

  return (
    <section
      className="flex flex-wrap items-center justify-between gap-space-3 rounded-md border border-border-light bg-white-warm px-space-4 py-space-3"
      aria-label="Can we reach the listing broker?"
      data-testid="broker-readiness-strip"
    >
      <div className="flex flex-wrap items-center gap-x-space-4 gap-y-space-2">
        <span className="text-small font-medium text-onyx" data-testid="broker-readiness-agent">
          {readiness.agentLabel}
        </span>
        <Fact ok={readiness.hasEmail} label="Email" testId="broker-readiness-email" />
        <Fact ok={readiness.hasPhone} label={phoneLabel} testId="broker-readiness-phone" />
        <Fact ok={readiness.hasContactForm} label="Contact form" testId="broker-readiness-form" />
      </div>

      {readiness.somethingMissing ? (
        <a
          href={readiness.researchPath}
          target="_blank"
          rel="noopener noreferrer"
          title={brokerOutreachCopy.findAgentHint}
          className={buttonVariants({ variant: "secondary", size: "sm" })}
          data-testid="broker-readiness-find-agent"
        >
          Find agent
        </a>
      ) : null}
    </section>
  );
}

function Fact({ ok, label, testId }: { ok: boolean; label: ReactNode; testId: string }) {
  const Icon = ok ? Check : X;
  return (
    <span
      className={`inline-flex items-center gap-space-1 text-small ${ok ? "text-status-success" : "text-status-warning"}`}
      data-testid={testId}
      data-ok={ok ? "true" : "false"}
    >
      <Icon size={14} strokeWidth={2} aria-hidden />
      <span className="text-onyx">{label}</span>
      <span className="sr-only">{ok ? "on file" : "missing"}</span>
    </span>
  );
}
